import { useEffect, useRef, useState } from "react";
import { testimonials } from "@/mocks/albertData";

export default function Testimonios() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActive((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const t = testimonials[active];

  return (
    <section id="testimonios" className="py-24 bg-[#0a0a0f] relative overflow-hidden" ref={ref}>
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-[#1a0a2e]/30 via-transparent to-[#1a0a2e]/20"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="text-[#c9a84c] text-sm tracking-widest uppercase font-medium mb-4">Lo que dicen</p>
          <h2 className="font-serif text-4xl md:text-5xl text-white font-bold mb-4">
            <span className="text-[#c9a84c]">Testimonios</span> Reales
          </h2>
          <p className="text-white/60 text-base max-w-xl mx-auto">
            Más de 5.000 personas han encontrado claridad y dirección con mis lecturas.
          </p>
        </div>

        {/* Main Testimonial Card */}
        <div className={`transition-all duration-1000 delay-200 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="max-w-4xl mx-auto bg-[#0f0f1a] border border-white/10 rounded-3xl overflow-hidden">
            <div className="flex flex-col md:flex-row">
              {/* Left - Avatar */}
              <div className="md:w-64 flex-shrink-0 bg-gradient-to-br from-[#1a0a2e] to-[#0a0a0f] flex flex-col items-center justify-center p-8 gap-4">
                <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-[#c9a84c]">
                  <img
                    src={t.avatar}
                    alt={t.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="text-center">
                  <p className="text-white font-semibold text-base">{t.name}</p>
                  <p className="text-white/50 text-xs mt-1">{t.location}</p>
                  <div className="flex justify-center gap-0.5 mt-2">
                    {[...Array(t.stars)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-[#c9a84c] text-sm"></i>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right - Text */}
              <div className="flex-1 p-8 md:p-10 flex flex-col justify-center">
                <i className="ri-double-quotes-l text-5xl text-[#c9a84c]/20 mb-4 block"></i>
                <p className="text-white/80 text-base md:text-lg leading-relaxed font-serif italic">
                  {t.text}
                </p>
              </div>
            </div>
          </div>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-8">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
                  i === active ? "bg-[#c9a84c] w-6" : "bg-white/30"
                }`}
              />
            ))}
          </div>
        </div>

        {/* Mini testimonials row */}
        <div className={`grid grid-cols-2 md:grid-cols-4 gap-4 mt-12 transition-all duration-1000 delay-400 ${visible ? "opacity-100" : "opacity-0"}`}>
          {testimonials.map((item, i) => (
            <button
              key={item.id}
              onClick={() => setActive(i)}
              className={`p-4 rounded-xl border text-left cursor-pointer transition-all duration-300 ${
                i === active
                  ? "border-[#c9a84c]/60 bg-[#c9a84c]/5"
                  : "border-white/10 bg-[#0f0f1a] hover:border-white/20"
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                  <img src={item.avatar} alt={item.name} className="w-full h-full object-cover" />
                </div>
                <p className="text-white text-xs font-semibold truncate">{item.name}</p>
              </div>
              <div className="flex gap-0.5">
                {[...Array(item.stars)].map((_, j) => (
                  <i key={j} className="ri-star-fill text-[#c9a84c] text-xs"></i>
                ))}
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
